import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.css';

// Universal environment detection for NPM packages
const getIsDevelopment = () => {
  // Method 1: Check for common development indicators (browser-safe)
  if (typeof window !== 'undefined') {
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('dev');

    const isDevelopmentPort =
      window.location.port &&
      (window.location.port.startsWith('3') ||
        window.location.port.startsWith('8'));

    if (isLocalhost || isDevelopmentPort) return true;
  }

  // Method 2: Check for __DEV__ global (React Native style)
  if (typeof globalThis !== 'undefined' && (globalThis as any).__DEV__) {
    return true;
  }

  // Method 3: Check for development build indicators
  if (typeof window !== 'undefined') {
    // Check if React is in development mode
    try {
      const isDev =
        (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ !== undefined;
      if (isDev) return true;
    } catch {
      // Continue to other checks
    }
  }

  // Default to production for safety
  return false;
};

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
  showErrorDetails?: boolean; // Allow manual control of error details display
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Always log error to console (works in both dev and production)
    console.error(
      `🛡️ ErrorBoundary caught error in ${
        this.props.componentName || 'Component'
      }:`,
      error,
      errorInfo
    );

    // Log environment for verification (universal approach)
    const isDev = this.props.showErrorDetails ?? getIsDevelopment();
    console.log(`📍 Environment: ${isDev ? 'Development' : 'Production'}`);

    // Call optional error handler (for production error reporting)
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary__content">
            <h3 className="error-boundary__title">
              Something went wrong
              {this.props.componentName
                ? ` in ${this.props.componentName}`
                : ''}
            </h3>
            <p className="error-boundary__message">
              We're sorry, but an unexpected error occurred. Please try
              refreshing or click the button below to retry.
            </p>
            <button
              className="error-boundary__retry"
              onClick={this.handleRetry}
            >
              Try Again
            </button>
            {(this.props.showErrorDetails ?? getIsDevelopment()) &&
              this.state.error && (
                <details className="error-boundary__details">
                  <summary>Error Details (Development Only)</summary>
                  <pre className="error-boundary__stack">
                    {this.state.error.message}
                    {'\n'}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
